/**
 * V4.44 — Cartographie des entreprises.
 *
 * Doctrine produit :
 *   "Dans quelle entreprise ai-je déjà une preuve de traction, mais pas encore
 *    assez de contacts internes ?"
 *
 * Niveau stratégique au-dessus des apporteurs individuels. Vise à transformer
 * `1 apporteur = 3 dossiers/an` en `1 agence = 15-30 dossiers/an`.
 *
 * Sections :
 *   - KPI globaux (entreprises, contacts, CA, dossiers)
 *   - Top 3 entreprises à pénétrer (potentiel élevé)
 *   - Top 3 entreprises bien adressées (potentiel faible — à entretenir)
 *   - Table complète triée par CA DESC (toutes les entreprises)
 */
import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import NetworkExpansionSection from "@/components/NetworkExpansionSection";
import { api, formatEUR } from "@/lib/api";
import {
  Buildings,
  Target,
  TrendUp,
  CheckCircle,
  HandWaving,
  Sparkle,
} from "@phosphor-icons/react";
import { BUSINESS_ACTIVITY_LABELS } from "@/lib/apporteurSegmentation";

const POTENTIAL_STYLES = {
  "élevé": { bg: "var(--cf-warning-soft)", color: "var(--cf-warning-text)", border: "var(--cf-warning-border)", label: "ÉLEVÉ" },
  "moyen": { bg: "var(--cf-primary-soft)", color: "var(--cf-primary-text)", border: "var(--cf-primary-border)", label: "MOYEN" },
  "faible": { bg: "var(--cf-bg-2)", color: "var(--cf-neutral-text)", border: "var(--cf-border-strong)", label: "FAIBLE" },
};

function PotentialBadge({ level }) {
  const s = POTENTIAL_STYLES[level] || POTENTIAL_STYLES.moyen;
  return (
    <span
      className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider border"
      style={{ background: s.bg, color: s.color, borderColor: s.border }}
      data-testid={`company-potential-${level}`}
    >
      {s.label}
    </span>
  );
}

function fmtPct(n) {
  if (n === null || n === undefined) return "—";
  return `${n}%`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" });
  } catch {
    return iso;
  }
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div
      className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-center gap-2 text-slate-400 mb-1.5">
        <Icon size={14} weight="fill" />
        <div className="text-[10px] font-extrabold uppercase tracking-wider">{label}</div>
      </div>
      <div className="text-[22px] font-extrabold text-slate-900 tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 font-semibold mt-0.5">{sub}</div>}
    </div>
  );
}

function CompanyRow({ company, rank }) {
  const lead = company.contacts?.[0];
  return (
    <tr className="border-t border-slate-100" data-testid={`company-row-${company.company_key}`}>
      <td className="px-3 py-3 text-slate-400 font-extrabold tabular-nums text-[12px] w-8">
        {rank !== undefined ? rank : ""}
      </td>
      <td className="px-2 py-3">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-extrabold text-slate-900 text-[13px]">{company.company_name}</span>
          <PotentialBadge level={company.penetration_potential} />
          {company.intros_available > 0 && (
            <span
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[9px] font-extrabold uppercase tracking-wider"
              style={{ background: "var(--cf-success-soft)", color: "var(--cf-success-text)", border: "1px solid var(--cf-success-border)" }}
              title={`${company.intros_available} contact(s) peuvent te présenter`}
            >
              <HandWaving size={9} weight="fill" /> Intro × {company.intros_available}
            </span>
          )}
        </div>
        <div className="text-[10.5px] text-slate-500 font-semibold mt-0.5 truncate">
          {company.business_activities?.map(
            (a) => BUSINESS_ACTIVITY_LABELS[a] || a
          ).join(" / ") || ""}
        </div>
      </td>
      <td className="px-2 py-3 text-right tabular-nums font-extrabold text-slate-700">
        {company.contacts_count}
      </td>
      <td className="px-2 py-3 text-right tabular-nums">
        <span className="font-extrabold text-slate-700">{company.dossiers_total}</span>
        {company.signed > 0 && (
          <span className="text-[10px] font-semibold text-emerald-700 ml-1">({company.signed} signé{company.signed > 1 ? "s" : ""})</span>
        )}
      </td>
      <td className="px-2 py-3 text-right tabular-nums font-extrabold text-emerald-700">
        {formatEUR(company.ca_net_total)}
      </td>
      <td className="px-2 py-3 text-slate-500 text-[12px] font-semibold">
        {fmtDate(company.latest_dossier_date)}
      </td>
      <td className="px-2 py-3 text-right tabular-nums font-bold">
        {fmtPct(company.closing_rate)}
      </td>
      <td className="px-2 py-3 text-[11px] text-slate-500 font-semibold max-w-[180px]">
        <span title={lead?.name || ""}>{lead?.name || "—"}</span>
      </td>
    </tr>
  );
}

function PenetrationCard({ company, rank }) {
  const lead = company.contacts?.[0];
  const reason = company.penetration_reasons?.[0] || "";
  return (
    <div
      className="bg-white border border-amber-200 rounded-2xl p-3 hover:shadow-md transition-shadow"
      data-testid={`company-penetrate-${company.company_key}`}
    >
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[11px] font-extrabold text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
          #{rank}
        </span>
        <span className="font-extrabold text-slate-900 text-[13.5px] flex-1 truncate">
          {company.company_name}
        </span>
        <PotentialBadge level={company.penetration_potential} />
      </div>
      <div className="flex items-center gap-3 text-[11px] text-slate-500 font-semibold flex-wrap mb-1">
        <span><b className="text-slate-800">{company.contacts_count}</b> contact{company.contacts_count > 1 ? "s" : ""}</span>
        <span>·</span>
        <span><b className="text-slate-800">{company.dossiers_total}</b> dossiers</span>
        <span>·</span>
        <span className="text-emerald-700 font-extrabold">{formatEUR(company.ca_net_total)}</span>
      </div>
      {reason && (
        <div className="text-[10.5px] text-slate-500 font-semibold mb-1.5 italic">
          {reason}
        </div>
      )}
      {lead && (
        <div className="text-[11px] font-bold text-slate-800 bg-amber-50 rounded-lg px-2 py-1.5 flex items-center gap-1.5">
          <Sparkle size={11} weight="fill" color="var(--cf-warning)" />
          <span>Demande à <b className="text-amber-900">{lead.name}</b> de te présenter ses collègues</span>
        </div>
      )}
    </div>
  );
}

export default function Entreprises() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/companies/insights");
        if (!cancelled) setData(data);
      } catch (err) {
        console.error("Companies insights error:", err);
        if (!cancelled) setError(err?.response?.data?.detail || "Erreur de chargement");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const totals = data?.totals || {};
  const top = data?.top_a_penetrer || [];
  const bien = data?.bien_adressees || [];
  const all = data?.companies || [];

  return (
    <Layout title="Entreprises" subtitle="Cartographie business">
      <div className="hidden md:block mb-6">
        <div className="text-[10.5px] font-extrabold text-slate-400 uppercase tracking-wider mb-1">
          Cartographie business
        </div>
        <h1 className="font-display text-4xl font-extrabold text-slate-900">Entreprises</h1>
        <p className="text-slate-500 mt-1">Quelle entreprise dois-je pénétrer plus profondément ?</p>
      </div>

      {/* KPI globaux */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="companies-kpis">
          <StatCard
            icon={Buildings}
            label="Entreprises"
            value={totals.companies_count || 0}
            sub={`${totals.contacts_in_companies || 0} contact${(totals.contacts_in_companies || 0) > 1 ? "s" : ""}`}
            testid="kpi-companies-count"
          />
          <StatCard
            icon={CheckCircle}
            label="Dossiers générés"
            value={totals.dossiers_total || 0}
            sub={`${totals.signed_total || 0} signé${(totals.signed_total || 0) > 1 ? "s" : ""}`}
            testid="kpi-companies-dossiers"
          />
          <StatCard
            icon={TrendUp}
            label="CA net total"
            value={formatEUR(totals.ca_net_total || 0)}
            sub="Tous portefeuilles confondus"
            testid="kpi-companies-ca"
          />
          <StatCard
            icon={Target}
            label="À pénétrer"
            value={top.length}
            sub="Potentiel élevé"
            testid="kpi-companies-top"
          />
        </div>
      )}

      {/* V4.45 — Moteur d'expansion réseau (en tête, doctrine business prioritaire) */}
      <NetworkExpansionSection />

      {/* Top 3 à pénétrer */}
      {top.length > 0 && (
        <section className="mb-6" data-testid="section-top-penetrer">
          <div className="flex items-baseline gap-2 mb-3">
            <Sparkle size={16} weight="fill" color="var(--cf-warning)" />
            <h2 className="text-[15px] font-extrabold text-slate-900">Top {Math.min(3, top.length)} entreprises à pénétrer</h2>
            <span className="text-[11px] text-slate-500 font-semibold">— preuve de traction, peu de contacts internes</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {top.slice(0, 3).map((c, i) => (
              <PenetrationCard key={c.company_key} company={c} rank={i + 1} />
            ))}
          </div>
        </section>
      )}

      {/* Top 3 bien adressées */}
      {bien.length > 0 && (
        <section className="mb-6" data-testid="section-bien-adressees">
          <div className="flex items-baseline gap-2 mb-3">
            <CheckCircle size={16} weight="fill" color="var(--cf-success-text)" />
            <h2 className="text-[15px] font-extrabold text-slate-900">Top {Math.min(3, bien.length)} entreprises bien adressées</h2>
            <span className="text-[11px] text-slate-500 font-semibold">— déjà bien pénétrées, à entretenir</span>
          </div>
          <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
            <table className="w-full text-[12.5px]">
              <tbody>
                {bien.map((c) => (
                  <tr key={c.company_key} className="border-b border-slate-100 last:border-b-0" data-testid={`bien-row-${c.company_key}`}>
                    <td className="px-3 py-2.5 font-extrabold text-slate-900">{c.company_name}</td>
                    <td className="px-2 py-2.5 text-[11px] text-slate-500 font-semibold tabular-nums">{c.contacts_count} contacts · {c.dossiers_total} dossiers</td>
                    <td className="px-2 py-2.5 text-right text-emerald-700 font-extrabold tabular-nums">{formatEUR(c.ca_net_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Table complète */}
      <section data-testid="section-all-companies">
        <h2 className="text-[15px] font-extrabold text-slate-900 mb-3">Toutes les entreprises</h2>

        {loading && (
          <div className="text-center text-slate-500 py-10" data-testid="companies-loading">
            Chargement…
          </div>
        )}

        {error && (
          <div className="text-center text-rose-600 py-10 font-semibold" data-testid="companies-error">
            {error}
          </div>
        )}

        {!loading && !error && all.length === 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl p-8 text-center" data-testid="companies-empty">
            <Buildings size={28} weight="thin" className="text-slate-300 mx-auto mb-3" />
            <div className="text-[14px] font-extrabold text-slate-900 mb-1">Aucune entreprise renseignée</div>
            <div className="text-[12px] text-slate-500 font-semibold max-w-md mx-auto leading-relaxed">
              Catégorise tes apporteurs en <b>Professionnels</b> depuis la page Apporteurs et renseigne leur entreprise pour faire apparaître ta cartographie business.
            </div>
          </div>
        )}

        {!loading && !error && all.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-2xl overflow-x-auto">
            <table className="w-full text-[12.5px]" data-testid="companies-table">
              <thead>
                <tr className="text-left text-[10.5px] font-bold text-slate-400 uppercase tracking-wider bg-slate-50/50">
                  <th className="px-3 py-2.5">#</th>
                  <th className="px-2 py-2.5">Entreprise · Potentiel</th>
                  <th className="px-2 py-2.5 text-right">Contacts</th>
                  <th className="px-2 py-2.5 text-right">Dossiers</th>
                  <th className="px-2 py-2.5 text-right">CA net</th>
                  <th className="px-2 py-2.5">Dernière reco</th>
                  <th className="px-2 py-2.5 text-right">Transfo</th>
                  <th className="px-2 py-2.5">Contact principal</th>
                </tr>
              </thead>
              <tbody>
                {all.map((c, i) => (
                  <CompanyRow key={c.company_key} company={c} rank={i + 1} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
}
